import React, { useEffect, useState, forwardRef } from 'react'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import { VisibilityOutlined, VisibilityOffOutlined } from '@mui/icons-material'
import styled from 'styled-components'
import PasswordHint from 'features/auth/components/PasswordHint'
import Collapse from '@mui/material/Collapse'
import { isNotNilOrEmpty } from 'utils/ramda'

const InputComponent = forwardRef((props, ref) => {
  const {
    name = '',
    type = 'text',
    onChange = () => {},
    validate = null,
    label = '',
    value: initialValue, // = ''
    required = false,
    variant = 'outlined',
    disabled = false,
    multiline = false,
    rows = 1,
    clearValueTrigger = '',
    startAdornment = null,
    withPasswordHint = false,
    additionalLabel = null,
    nomarginbottom = false,
    optional = false,
    inputProps = {},
    autoComplete = undefined,
    placeholder = '',
    onBlur = () => {},
    onFocus = () => {},
    hasError
  } = props
  const [passwordVisible, _setPasswordVisibility] = useState(false)
  const [passwordHintVisible, setPasswordHintVisible] = useState(false)
  const [touched, _setTouched] = useState(false)
  const [value, _setValue] = useState(initialValue || '')
  const [{ valid, error }, _validate] = useState({
    valid: !validate,
    error: ''
  })

  useEffect(() => {
    validate &&
      validate(name, v => {
        _validate({ valid: v.valid, error: v.errors })
      })
  }, [value])

  useEffect(() => {
    type === 'date' ? _setValue(null) : _setValue('')
  }, [clearValueTrigger])

  useEffect(() => {
    _setValue(initialValue)
  }, [initialValue])

  const inputType = !type || passwordVisible ? 'text' : type

  const handleVisibilityChange = () =>
    _setPasswordVisibility(prevVisibility => !prevVisibility)

  const getEndAdornment = type === 'password' && (
    <InputAdornment position='end'>
      <IconButton onClick={handleVisibilityChange} size='large'>
        {passwordVisible ? <VisibilityOutlined /> : <VisibilityOffOutlined />}
      </IconButton>
    </InputAdornment>
  )

  const handleBlur = e => {
    _setTouched(true)
    type === 'password' && withPasswordHint && setPasswordHintVisible(false)
    typeof onBlur === 'function' && onBlur(e?.target?.value, name)
  }
  const handleFocus = () => {
    typeof onFocus === 'function' && onFocus()
    type === 'password' && withPasswordHint && setPasswordHintVisible(true)
  }
  const handleChange = e => {
    _setValue(e.target.value)
    onChange(name, e.target.value)
  }

  return (
    <InputWrapper nomarginbottom={nomarginbottom}>
      {isNotNilOrEmpty(label) && (
        <Label>
          {valid || (!valid && !touched) || disabled ? (
            optional ? (
              <LabelWrapper>
                {label}
                <span>(optional)</span>
              </LabelWrapper>
            ) : (
              <div>{label}</div>
            )
          ) : (
            <Error>{error}</Error>
          )}
          {additionalLabel && additionalLabel}
        </Label>
      )}

      <TextField
        ref={ref}
        disabled={disabled}
        placeholder={placeholder}
        variant={variant}
        required={required}
        name={name}
        multiline={multiline}
        minRows={rows}
        value={value}
        type={inputType || 'text'}
        autoComplete={autoComplete}
        error={((!valid && touched) || hasError) && !disabled}
        fullWidth
        onBlur={handleBlur}
        onFocus={handleFocus}
        onChange={handleChange}
        InputProps={{
          startAdornment: <Adornment>{startAdornment}</Adornment>,
          endAdornment: getEndAdornment,
          style: {
            backgroundColor: disabled ? '#fcfcfc' : '#fff',
            height: multiline ? 'auto' : '45px'
          }
        }}
        inputProps={inputProps}
        style={{
          width: '100%'
        }}
      />
      <Collapse in={passwordHintVisible}>
        <PasswordHint password={value} />
      </Collapse>
    </InputWrapper>
  )
})

export const Input = InputComponent
export default InputComponent

const InputWrapper = styled.div`
  position: relative;
  margin-bottom: ${({ nomarginbottom }) => (nomarginbottom ? 0 : '25px')};

  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors.secondary};
  }
`

const Label = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 7px;
  font-weight: bold;
  font-size: 12px;
  letter-spacing: 0.11px;
  min-height: 17px;
`

const Error = styled.span`
  color: ${({ theme }) => theme.colors.error};
`

const LabelWrapper = styled.div`
  span {
    display: inline-block;
    margin-left: 5px;
    color: ${({ theme }) => theme.colors.grey[700]};
    font-size: 12px;
  }
`

const Adornment = styled.div`
  margin-right: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.grey[700]};
`
